import {
  Box,
  Button,
  Image,
  Text,
  VStack,
  Heading,
  SimpleGrid,
  useColorModeValue,
} from "@chakra-ui/react";
import bearImg from "../assets/bear-help.webp";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

import { HoverMenuCard } from "./home/components/HoverMenuCard";
import { Hero } from "./home/components/Hero";
import { MENU_SECTIONS, isItemVisible, type MenuItem } from "../config/menu";
import { hasRole } from "../lib/auth";
import { usePermissionsContext } from "../data/permissions/permissionContext";

const cardColors = [
  "blue.500",
  "purple.500",
  "teal.500",
  "green.500",
  "orange.500",
  "pink.500",
  "cyan.500",
  "red.500",
];

const HomePage = () => {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const { permissions } = usePermissionsContext();

  const filterItems = (items: MenuItem[]): MenuItem[] =>
    items
      .filter((item) => isItemVisible(item, permissions))
      .map((item) =>
        item.subitems ? { ...item, subitems: filterItems(item.subitems) } : item
      );

  const visibleSections = MENU_SECTIONS.flatMap((section) => {
    if (section.requiredRole && !hasRole(section.requiredRole)) return [];
    if (section.requiredPermissions?.length && !section.requiredPermissions.some((p) => permissions.includes(p))) return [];

    const visibleItems = filterItems(section.subitems ?? []);

    if (visibleItems.length === 0) return [];
    return [{ ...section, subitems: visibleItems }];
  });
  const helpBg = useColorModeValue("white", "#0f1f3a");
  const helpText = useColorModeValue("gray.600", "#98a2b3");

  return (
    <VStack spacing={10} px={{ base: 6, md: 12 }} py={10} align="stretch" position="relative">
      <Hero />

      <Box w="100%">
        <SimpleGrid
          w="100%"
          columns={{ base: 1, md: 2, xl: 3 }}
          spacing={{ base: 6, md: 8, lg: 10 }}
        >
          {visibleSections.map((section, index) => (
            <Box key={section.name}>
              <HoverMenuCard
                title={section.name}
                description={`Explore as opções de ${section.name.toLowerCase()}`}
                items={section.subitems ?? []}
                navigate={navigate}
                icon={section.icon}
                color={cardColors[index % cardColors.length]}
              />
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      <Button
        position="fixed"
        bottom="30px"
        right="30px"
        zIndex="999"
        borderRadius="full"
        width="72px"
        height="72px"
        p={0}
        overflow="hidden"
        bg="transparent"
        border="none"
        boxShadow="xl"
        minW="unset"
        _hover={{ transform: "scale(1.06)" }}
        _active={{ transform: "scale(0.97)" }}
        transition="transform 0.15s"
        onClick={() => setHelpOpen((prev) => !prev)}
      >
        <Image
          src={bearImg}
          alt="Ajuda"
          w="100%"
          h="100%"
          objectFit="cover"
          objectPosition="center top"
          draggable={false}
        />
      </Button>

      {helpOpen && (
        <Box
          position="fixed"
          bottom="110px"
          right="30px"
          bg={helpBg}
          borderRadius="xl"
          boxShadow="2xl"
          p={4}
          width="300px"
          zIndex="999"
        >
          <Heading as="h4" size="sm" mb={2}>
            Precisa de ajuda?
          </Heading>
          <Text fontSize="sm" color={helpText}>
            Em caso de dúvidas, entre em contato com o suporte interno.
          </Text>
        </Box>
      )}
    </VStack>
  );
};

export default HomePage;
